import React, { useState } from 'react';
import { Deployment } from '../../types/api';
import { DeploymentStatus } from './DeploymentStatus';
import { Loader2, AlertCircle, Filter } from 'lucide-react';

interface DeploymentListProps {
  deployments: Deployment[];
  loading?: boolean;
  error?: string;
  onCancel?: (deploymentId: string) => void;
  onRollback?: (deploymentId: string) => void;
}

type StatusFilter = 'all' | 'pending' | 'in_progress' | 'completed' | 'failed' | 'rolled_back';

export function DeploymentList({
  deployments,
  loading = false,
  error,
  onCancel,
  onRollback,
}: DeploymentListProps) {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [searchTerm, setSearchTerm] = useState('');

  const filteredDeployments = deployments.filter((deployment) => {
    // Status filter
    if (statusFilter !== 'all' && deployment.status !== statusFilter) {
      return false;
    }

    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchesFeature = deployment.featureName.toLowerCase().includes(search);
      const matchesId = deployment.id.toLowerCase().includes(search);
      const matchesRepo = deployment.repoName?.toLowerCase().includes(search);
      if (!matchesFeature && !matchesId && !matchesRepo) {
        return false;
      }
    }

    return true;
  });

  const statusCounts = {
    all: deployments.length,
    pending: deployments.filter((d) => d.status === 'pending').length,
    in_progress: deployments.filter((d) => d.status === 'in_progress').length,
    completed: deployments.filter((d) => d.status === 'completed').length,
    failed: deployments.filter((d) => d.status === 'failed').length,
    rolled_back: deployments.filter((d) => d.status === 'rolled_back').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-3 text-gray-600">Loading deployments...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
        <div>
          <h3 className="font-medium text-red-900">Error loading deployments</h3>
          <p className="text-sm text-red-700 mt-1">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center gap-3 mb-4">
          <Filter className="h-5 w-5 text-gray-500" />
          <h3 className="font-medium text-gray-900">Filters</h3>
        </div>

        {/* Search */}
        <div className="mb-4">
          <input
            type="text"
            placeholder="Search by feature name, ID, or repository..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Status Tabs */}
        <div className="flex flex-wrap gap-2">
          {(Object.keys(statusCounts) as StatusFilter[]).map((status) => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-4 py-2 rounded-lg font-medium text-sm capitalize transition-colors ${
                statusFilter === status
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {status.replace('_', ' ')} ({statusCounts[status]})
            </button>
          ))}
        </div>
      </div>

      {/* Results Summary */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          Showing {filteredDeployments.length} of {deployments.length} deployments
        </p>
      </div>

      {/* Deployment List */}
      {filteredDeployments.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <p className="text-gray-500">No deployments found matching your filters.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredDeployments.map((deployment) => (
            <DeploymentStatus
              key={deployment.id}
              deployment={deployment}
              onCancel={onCancel}
              onRollback={onRollback}
            />
          ))}
        </div>
      )}
    </div>
  );
}
